import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import get from 'lodash/get';
import { getToken, removeToken } from '@Hotels-at-Home/hah-fe-core/dist/services/auth';
import { privateQuery, GetCustomerInfo } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import { setShowCookiesPopup } from '@Hotels-at-Home/hah-fe-core/dist/store/cookieSettings';
import { createWishlist } from '@Hotels-at-Home/hah-fe-core/dist/store/wishlist';
import { changeAuth, changeIsFulfilled } from '@Hotels-at-Home/hah-fe-core/dist/store/user';
import { getCartItemsAmount } from '@Hotels-at-Home/hah-fe-core/dist/store/cart/thunks';
import { getCurrency } from '@Hotels-at-Home/hah-fe-core/dist/store/currencies/thunks';
import { getCookie, extractErrorMessage } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { COOKIE_NOTICE_NAME } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { IFooter, INavMenu } from '@Hotels-at-Home/hah-fe-core/dist/types';

import { useDispatch } from '~/store/hooks';
import { AppDispatch } from '~/store';
import apolloConfig from '~/constants/apolloConfig';

interface IError {
  message: string;
  statusCode: number;
}

export interface IAppProps {
  navMenu: INavMenu;
  footer: IFooter;
  termsAndConditions: string;
  userAgent: string | undefined;
  error: IError;
}

const clientRequest = async (dispatch: AppDispatch, setError: (error: IError) => void) => {
  try {
    const cookieNotice = (getCookie(COOKIE_NOTICE_NAME) || '') as string;

    dispatch(setShowCookiesPopup(!cookieNotice));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [dispatch(getCartItemsAmount()), dispatch(getCurrency())];

    const token = getToken();

    if (token) {
      const userReq = privateQuery(
        apolloConfig(),
        {
          query: GetCustomerInfo,
        },
        'GetCustomerInfo',
      );

      promisesList.push(userReq);
    } else {
      // If no token - set auth "isFulfilled = true" prop anyway
      // This status is used on the WishlistButton to detect if a user request processed
      dispatch(changeIsFulfilled(true));
    }

    const [cartItemsAmountResultAction, currencyResultAction, userRes] = await Promise.all(
      promisesList,
    );

    if (getCartItemsAmount.rejected.match(cartItemsAmountResultAction)) {
      setError({
        message: cartItemsAmountResultAction.payload.errorMessage,
        statusCode: null,
      });
    }

    if (getCurrency.rejected.match(currencyResultAction)) {
      setError({
        message: currencyResultAction.payload.errorMessage,
        statusCode: null,
      });
    }

    const user = get(userRes, ['data', 'customer']);

    if (user) {
      const { wishlists, ...currentUser } = user;
      dispatch(createWishlist(wishlists));
      dispatch(changeAuth(currentUser));
    } else {
      removeToken();
      dispatch(changeIsFulfilled(true));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    setError({
      message: extractErrorMessage(e),
      statusCode: e?.networkError?.statusCode,
    });
  }
};

// Client hook
export const useClientRequest = () => {
  const [error, setError] = useState<IError>({
    message: null,
    statusCode: null,
  });

  const { asPath, locale } = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    clientRequest(dispatch, setError);
  }, [asPath, locale]); // Run on each route path and locale change

  return { error };
};
